import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { AbilityServiceSignal } from '@casl/angular';
import { Store } from '@ngxs/store';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  ActivityRow,
  Label,
  OperationStatus,
  Project,
  RoleAction,
  RolePermissionSubject,
  SHIFT_WIDTH_ADJUSTMENT_FOR_ACTIVITIES_PX,
  ShiftColorOptions,
  StaffLayoutStyle,
  UnassignedShift,
} from '@wilson/interfaces';
import { LabelsState } from '@wilson/labels';
import { IGNORE_ON_CLICK_CLASS_NAME } from '@wilson/non-domain-specific/ng-zorro/nz-drawer/core';
import { ProjectsState } from '@wilson/projects/state';
import {
  AssignmentShiftCardComponent,
  DeferredAssignmentShiftCardHelperService,
  GanttDateToPositionPxPipe,
  SelectedItemOutput,
  ShiftCardTooltipStateService,
  ShiftsIconRenderingHelperService,
  TimeDifferenceWidthPxPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { RowsToActivitiesPipe } from '@wilson/shift-timeline/pipes';
import { DetermineShiftRenderDatetimePipe } from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
  ShiftTimelineUnassignedRegionState,
} from '@wilson/shift-timeline/state';
import { Observable, of } from 'rxjs';
import { GanttActivityDragHandlerHelperService } from '../../../services/gantt-activity-drag-handler-helper.service';
import { GanttActivityResizeHandlerHelperService } from '../../../services/gantt-activity-resize-handler-helper.service';
import { TimelineShiftDragAndDropDirectiveHelperService } from '../../../services/timeline-shift-drag-and-drop-directive-helper.service';
import { UnassignedActivityDrawerService } from '../../../services/unassigned-activity-drawer.service';
import { ResizeActivityHelperService } from '../../timeline-auto-size-shift-with-activities-card/TimelineAutoSizeActivityCard/resize-activity-helper.service';
import { UnassignedShiftsWithoutActivitiesDragHandlerDirective } from '../../timeline-directives/unassigned-shifts-without-activities-drag-handler.directive';

@Component({
  selector: 'wilson-unassigned-shift-without-activities',
  imports: [
    CommonModule,
    AssignmentShiftCardComponent,
    DetermineShiftRenderDatetimePipe,
    UnassignedShiftsWithoutActivitiesDragHandlerDirective,
    TimeDifferenceWidthPxPipe,
    AuthorizationModule,
    RowsToActivitiesPipe,
    FeatureFlagsModule,
    GanttDateToPositionPxPipe,
  ],
  templateUrl: './unassigned-shift-without-activities.component.html',
  styleUrl: './unassigned-shift-without-activities.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ShiftCardTooltipStateService, ResizeActivityHelperService],
})
export class UnassignedShiftWithoutActivitiesComponent
  implements OnInit, OnChanges, AfterViewInit
{
  private readonly store = inject(Store);
  private readonly unassignedActivityDrawerService = inject(
    UnassignedActivityDrawerService,
  );
  readonly shiftCardTooltipStateService = inject(ShiftCardTooltipStateService);
  readonly timelineShiftDragAndDropDirectiveHelperService = inject(
    TimelineShiftDragAndDropDirectiveHelperService,
  );
  readonly ganttActivityDragHandlerHelperService = inject(
    GanttActivityDragHandlerHelperService,
  );
  readonly ganttActivityResizeHandlerHelperService = inject(
    GanttActivityResizeHandlerHelperService,
  );
  readonly deferredAssignmentShiftCardHelperService = inject(
    DeferredAssignmentShiftCardHelperService,
  );
  private readonly shiftsIconRenderingHelperService = inject(
    ShiftsIconRenderingHelperService,
  );

  OperationStatus = OperationStatus;

  @Input()
  shiftColorOption!: ShiftColorOptions;

  @Input()
  isMoveMode!: boolean;

  @Input()
  isAssignmentMode!: boolean;

  @Input()
  shift!: UnassignedShift;

  @Input()
  isLastShiftInLastRow!: boolean;

  @Input()
  shiftHeight!: number;

  @Input()
  staffLayoutStyle!: StaffLayoutStyle;

  shiftIdsToHighlight$ = this.store.select(
    ShiftTimelineDataState.shiftIdsToHighlight,
  );
  timelineWidthPixel$ = this.store.select(ShiftTimelineState.frameLength);
  interval$ = this.store.select(ShiftTimelineDataState.timeframe);
  areActivitiesVisible$ = this.store.select(
    ShiftTimelineSettingsState.areActivitiesVisible,
  );
  shiftActivitiesRows$!: Observable<ActivityRow[]>;
  StaffLayoutStyle = StaffLayoutStyle;
  IGNORE_ON_CLICK_CLASS_NAME = IGNORE_ON_CLICK_CLASS_NAME;

  protected SHIFT_WIDTH_ADJUSTMENT_FOR_ACTIVITIES_PX =
    SHIFT_WIDTH_ADJUSTMENT_FOR_ACTIVITIES_PX;
  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;
  protected project$!: Observable<Project | null | undefined>;
  protected label$!: Observable<Label | null | undefined>;
  protected loadIconsImmediately$!: Observable<boolean>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['shift']) {
      this.shiftCardTooltipStateService.initState(null, this.shift);
      this.project$ = this.shift.projectId
        ? this.store.select(ProjectsState.projectById(this.shift.projectId))
        : of(null);

      this.label$ = this.shift.labels?.length
        ? this.store.select(LabelsState.getLabelById(this.shift.labels[0].id))
        : of(null);
    }
  }

  ngAfterViewInit(): void {
    if (this.isLastShiftInLastRow) {
      this.shiftsIconRenderingHelperService.markLastShiftAsRenderedForUnassignedRegion();
    }
  }

  ngOnInit(): void {
    this.loadIconsImmediately$ =
      this.shiftsIconRenderingHelperService.isLastShiftRenderedInUnassignedRegion$;
    this.shiftActivitiesRows$ = this.store.select(
      ShiftTimelineUnassignedRegionState.shiftActivitiesRowsOf(this.shift.id),
    );
  }

  async openActivityDrawer(event: SelectedItemOutput): Promise<void> {
    await this.unassignedActivityDrawerService.openActivityDrawer(event);
  }
}
